import React from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Descriptions, Icon } from 'antd';
import Confirm from '../Confirm';

const modalStyles = `
@keyframes modal-pop { 
  from{opacity:0;transform:scale(0.96) translateY(12px)} 
  to{opacity:1;transform:scale(1) translateY(0)} 
}
@keyframes cd-glow { 
  0%,100%{box-shadow:0 0 0 0 rgba(24,144,255,0.4)} 
  50%{box-shadow:0 0 0 10px rgba(0,0,0,0)} 
}
@keyframes cd-bar { 
  0%{background-position:0% center} 
  100%{background-position:200% center} 
}

.cd-modal .ant-modal-content { 
  border-radius:14px!important; 
  overflow:hidden!important; 
  box-shadow:0 24px 64px rgba(0,0,0,.16),0 0 0 1px rgba(24,144,255,.1)!important; 
  animation:modal-pop .3s cubic-bezier(.22,1,.36,1) both!important; 
}
.cd-modal .ant-modal-header { 
  padding:0!important; 
  border-bottom:none!important; 
}
.cd-modal .ant-modal-body { 
  padding:0!important; 
}
.cd-modal .ant-modal-footer { 
  display:none!important; 
}
.cd-modal .ant-modal-close { 
  top:14px!important; 
  right:16px!important; 
  color:rgba(255,255,255,0.8)!important; 
  transition:color .2s,transform .2s!important; 
}
.cd-modal .ant-modal-close:hover { 
  color:#fff!important; 
  transform:rotate(90deg)!important; 
}
.cd-modal .ant-modal-close .ant-modal-close-x { 
  width:36px!important; 
  height:36px!important; 
  line-height:36px!important; 
}

.cd-header { 
  background:linear-gradient(135deg,#003a8c 0%,#0050b3 35%,#1890ff 70%,#096dd9 100%); 
  padding:18px 22px 16px; 
  display:flex; 
  align-items:center; 
  gap:14px; 
  position:relative; 
  overflow:hidden; 
}
.cd-header::before { 
  content:''; 
  position:absolute; 
  inset:0; 
  background-image:radial-gradient(rgba(255,255,255,.09) 1px,transparent 1px); 
  background-size:20px 20px; 
  pointer-events:none; 
}
.cd-header::after { 
  content:''; 
  position:absolute; 
  bottom:0; 
  left:0; 
  right:0; 
  height:3px; 
  background:linear-gradient(90deg,#13c2c2,#1890ff,#52c41a,#fa8c16,#13c2c2); 
  background-size:300% auto; 
  animation:cd-bar 3s linear infinite; 
}
.cd-header-icon { 
  width:44px; 
  height:44px; 
  border-radius:12px; 
  background:rgba(255,255,255,.15); 
  border:1.5px solid rgba(255,255,255,.25); 
  display:flex; 
  align-items:center; 
  justify-content:center; 
  flex-shrink:0; 
  animation:cd-glow 2.6s ease-in-out infinite; 
}
.cd-header-title { 
  font-size:15px; 
  font-weight:800; 
  color:#fff; 
  letter-spacing:.8px; 
}
.cd-header-sub { 
  font-size:11px; 
  color:rgba(255,255,255,.6); 
  letter-spacing:1px; 
  margin-top:2px; 
}

.cd-body { 
  padding:20px 22px; 
  max-height:70vh; 
  overflow-y:auto; 
}
.cd-body .ant-descriptions-title { 
  font-size:14px; 
  font-weight:700; 
  color:#1890ff; 
  margin-bottom:16px; 
}
.cd-body .ant-descriptions-bordered .ant-descriptions-item-label { 
  background:#fafcff; 
  font-weight:600; 
  color:#595959; 
}
.cd-body .ant-descriptions-bordered .ant-descriptions-item-content { 
  background:#fff; 
  color:#262626; 
}

.cd-footer { 
  padding:16px 24px; 
  border-top:1px solid #f0f0f0; 
  text-align:right; 
  background:#fafcff; 
  border-radius:0 0 14px 14px; 
}
.cd-footer .ant-btn { 
  margin-left:12px; 
  border-radius:7px; 
  font-weight:600; 
  transition:all .2s; 
}
.cd-footer .ant-btn-red { 
  background:#fff1f0; 
  border-color:#ffa39e; 
  color:#cf1322; 
}
.cd-footer .ant-btn-red:hover { 
  background:#ff4d4f; 
  border-color:#ff4d4f; 
  color:#fff; 
  transform:translateY(-1px); 
}
.cd-footer .ant-btn-primary { 
  background:#1890ff; 
  border-color:#1890ff; 
}
.cd-footer .ant-btn-primary:hover { 
  background:#40a9ff; 
  border-color:#40a9ff; 
  transform:translateY(-1px); 
}
`;

const CustomerDetails = props => {
  const { data, closeModal, visible, updateCustomer, setLoading } = props;

  // Get status for badge display
  const getStatusBadge = () => {
    const statusMap = {
      'PENDING APPROVAL': { color: '#fa8c16', bg: '#fff7e6', border: '#ffd591', label: 'PENDING' },
      'REJECTED': { color: '#ff4d4f', bg: '#fff1f0', border: '#ffa39e', label: 'REJECTED' },
      'RECOMMENDED': { color: '#13c2c2', bg: '#e6fffb', border: '#87e8de', label: 'RECOMMENDED' },
      'APPROVED': { color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f', label: 'APPROVED' },
    };
    const status = statusMap[data.isApproved] || statusMap['PENDING APPROVAL'];
    return (
      <span style={{ 
        display: 'inline-flex', 
        alignItems: 'center', 
        gap: 6,
        padding: '4px 12px',
        borderRadius: 20,
        background: status.bg,
        border: `1px solid ${status.border}`,
        color: status.color,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '.5px'
      }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: status.color, boxShadow: `0 0 5px ${status.color}` }} />
        {status.label}
        {data.comment && (
          <Icon type="info-circle" style={{ marginLeft: 4, fontSize: 12 }} />
        )}
      </span>
    );
  };

  return (
    <>
      <style>{modalStyles}</style>
      <Modal
        className="cd-modal"
        visible={visible}
        footer={null}
        width={750}
        onCancel={closeModal}
        title={
          <div className="cd-header">
            <div className="cd-header-icon">
              <Icon type="shop" style={{ fontSize: 20, color: '#fff' }} />
            </div>
            <div>
              <div className="cd-header-title">BUSINESS INFORMATION</div>
              <div className="cd-header-sub">Customer Profile Details</div>
            </div>
            <div style={{ marginLeft: 'auto' }}>
              {getStatusBadge()}
            </div>
          </div>
        }
      >
        <div className="cd-body">
          <Descriptions column={2} size="small" bordered>
            <Descriptions.Item span={2} label="Business name">
              <strong style={{ color: '#1890ff' }}>{data.bi_name}</strong>
            </Descriptions.Item>
            <Descriptions.Item span={2} label="Business address">
              {data.bi_address}
            </Descriptions.Item>
            <Descriptions.Item span={2} label="Nature of business">
              {data.bi_nature}
            </Descriptions.Item>
            <Descriptions.Item span={1} label="Tel No.">
              {data.bi_tel || <Icon type="ellipsis" style={{ color: '#d9d9d9' }} />}
            </Descriptions.Item>
            <Descriptions.Item span={1} label="Fax No.">
              {data.bi_fax || <Icon type="ellipsis" style={{ color: '#d9d9d9' }} />}
            </Descriptions.Item>
            <Descriptions.Item span={1} label="SSS No.">
              {data.bi_sss || <Icon type="ellipsis" style={{ color: '#d9d9d9' }} />}
            </Descriptions.Item>
            <Descriptions.Item span={1} label="Tin No.">
              {data.bi_tin || <Icon type="ellipsis" style={{ color: '#d9d9d9' }} />}
            </Descriptions.Item>
            <Descriptions.Item span={2} label="Email address">
              {data.bi_email ? (
                <a href={`mailto:${data.bi_email}`} style={{ color: '#1890ff', fontWeight: 500 }}>
                  {data.bi_email}
                </a>
              ) : (
                <Icon type="ellipsis" style={{ color: '#d9d9d9' }} />
              )}
            </Descriptions.Item>
            <Descriptions.Item span={2} label="Years in business">
              {data.bi_yearsBusiness || <Icon type="ellipsis" style={{ color: '#d9d9d9' }} />}
            </Descriptions.Item>
            <Descriptions.Item span={2} label="Business Premises">
              {data.bi_premises || <Icon type="ellipsis" style={{ color: '#d9d9d9' }} />}
            </Descriptions.Item>
            <Descriptions.Item span={2} label="Business Type">
              {data.bi_type || <Icon type="ellipsis" style={{ color: '#d9d9d9' }} />}
            </Descriptions.Item>
            <Descriptions.Item span={1} label="Contact Person">
              {data.bi_contact || <Icon type="ellipsis" style={{ color: '#d9d9d9' }} />}
            </Descriptions.Item>
            <Descriptions.Item span={1} label="Position">
              {data.bi_contactposition || <Icon type="ellipsis" style={{ color: '#d9d9d9' }} />}
            </Descriptions.Item>
          </Descriptions>
        </div>
        
        {data.isApproved === 'PENDING APPROVAL' && (
          <div className="cd-footer">
            <Button
              onClick={() => {
                Confirm(
                  updateCustomer,
                  null,
                  { id: data.id, method: 'rejected', src: 'om' },
                  setLoading,
                  closeModal
                );
              }}
              className="ant-btn-red"
              icon="close-circle"
            >
              Reject
            </Button>
            <Button
              icon="check-circle"
              type="primary"
              onClick={() => {
                Confirm(
                  updateCustomer,
                  null,
                  { id: data.id, method: 'approved', src: 'om' },
                  setLoading,
                  closeModal
                );
              }}
            >
              Approve
            </Button>
          </div>
        )}
      </Modal>
    </>
  );
};

CustomerDetails.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  visible: PropTypes.bool.isRequired,
  updateCustomer: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
};

export default CustomerDetails;